<script setup>
import { ref, computed, onMounted, defineAsyncComponent } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
/***/
import { useAuthUser } from '@compositions/Auth';
/***/
import { useApplicationStore } from '@stores/application';
import { useWriteTaskStore } from '@stores/writeTask';
import { useHistoryStore } from '@stores/history';
/***/
import DateHelper from '@classes/DateHelper';

// Composants
const Layout = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Layouts/Authenticated'));
const Wrapper = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Me/Task/Write/Wrapper'));

// Route et Router
const router = useRouter();
const route = useRoute();

// I18n
const { t, locale } = useI18n();

// Store
const applicationStore = useApplicationStore();
const writeTaskStore = useWriteTaskStore();
const historyStore = useHistoryStore();

// Retourne les données de la tâche planifiée en query
const fromPlannedTaskPublicId = computed(() => {
    return (route.query['from-planned-task'] || null);
});
const fromPlannedTask = computed(() => {
    const fromPublicId = fromPlannedTaskPublicId.value;
    if(fromPublicId === null) {
        return null;
    }
    return writeTaskStore.getByPublicId(fromPublicId);
});

// Retourne l'application courante
const currentApplication = computed(() => applicationStore.getCurrent);

// Composition pour l'utilisateur connecté
const { authUser, authUserHasPermission } = useAuthUser({});

// Vrai si l'utilisateur est autorisée à accéder à la page
const allowed = ref(false);

onMounted(() => {
    const canConsult = (authUser.value.type === 'CUSTOMER' && authUserHasPermission('create task'));
    if(! canConsult) {
        router.push({
            name: 'error',
            params: {
                statusCode: 404
            }
        });
    } else {

        // Chargement de la tâche qu'on vient de planifier si en query et non chargée
       if(fromPlannedTaskPublicId.value !== null && fromPlannedTask.value === null) {
            writeTaskStore.loadTaskByPublicId(fromPlannedTaskPublicId.value).then(() => {
                // On supprime le message après 20 secondes
                setTimeout(() => {
                    const query = route.query;
                    historyStore.setMustPreserveScrollPosition(true);
                    delete query['from-planned-task'];
                    router.replace({
                        query
                    });

                }, 20000);
            });
        }

        // On réinitialise la tâche courante
        writeTaskStore.setCurrent(null);
        allowed.value = true;
        document.title = t('page.me.task.write.title') + ' - ' + currentApplication.value.name;
    }
})
</script>

<template>
    <layout v-if="allowed">
        <p
            v-if="fromPlannedTask !== null"
            class="generic-message generic-message-success"
        >
            {{
                $t('task.write.planned.success', {
                    taskTitle: fromPlannedTask.title,
                    date: DateHelper.formatted(fromPlannedTask.plannedEndedAt, locale),
                    time: DateHelper.formatted(fromPlannedTask.plannedEndedAt, locale, {
                        hour: 'numeric',
                        minute: 'numeric'
                    })
                })
            }}
        </p>
        <wrapper :load-draft-list="true"></wrapper>
    </layout>
</template>
